import { useCallback } from 'react';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { SharingRequest } from '../chart-permissions';
import { ChartHandle } from '../chart-handle';

/**
 * A one-shot "the user asked, via chat, to change who this is shared with" note, carried in the
 * react-query cache from its writer (`AgentDrawer`, which sees the `set_sharing` tool call) to its
 * consumer (the surface that owns the record's sharing control).
 *
 * A shared-cache signal rather than a save inside the drawer, for the same reason
 * `usePendingPromotion` is one: the surface owns the action, so the surface owns every gate on it. A
 * chat sharing change therefore meets the same permission check, the same ceiling and the same
 * offered lists as the dialog, and the two paths cannot drift.
 *
 * ⚠️ THE DUPLICATION WITH `usePendingPromotion` IS DELIBERATE. That file holds the same cache
 * machinery for a different value type, and the two were kept separate on purpose rather than sharing
 * a generic: the promotion note is merged code on a path two other flows already depend on, and
 * unifying them buys nothing a user can see. If you are here to remove the repetition, that is the
 * trade that was already made — leave it.
 *
 * No `sessionStorage` sidecar: a transient intent, not durable state. `staleTime`/`gcTime` of
 * `Infinity` keep it alive across a remount of the consuming view while the request is pending.
 */
const pendingSharingKey = (company: string, target: string) => ['pending-sharing', company, target];

/**
 * A dashboard's note target. A chart uses its `ChartHandle` directly, which is already unique
 * (`chart:<uuid>` or `draft:<uuid>`); a dashboard id carries no such prefix, so it gets one here. One
 * target shape for both records keeps the seed helper single.
 */
export const sharingTargetKey = (dashboardId: string): string => `dashboard:${dashboardId}`;

export interface PendingSharing {
  pendingSharing: SharingRequest | null;
  setPendingSharing: (request: SharingRequest) => void;
  clearPendingSharing: () => void;
}

export const usePendingSharing = (
  company: string,
  target: ChartHandle | string | null
): PendingSharing => {
  const queryClient = useQueryClient();
  const key = target ? pendingSharingKey(company, target) : ['pending-sharing', company, null];

  const { data: pendingSharing = null } = useQuery<SharingRequest | null>({
    queryKey: key,
    queryFn: () => queryClient.getQueryData<SharingRequest>(key) ?? null,
    enabled: Boolean(company && target),
    staleTime: Infinity,
    gcTime: Infinity
  });

  const setPendingSharing = useCallback(
    (request: SharingRequest) => {
      if (target) {
        queryClient.setQueryData(pendingSharingKey(company, target), request);
      }
    },
    [queryClient, company, target]
  );

  const clearPendingSharing = useCallback(() => {
    if (target) {
      queryClient.setQueryData(pendingSharingKey(company, target), null);
    }
  }, [queryClient, company, target]);

  return { pendingSharing, setPendingSharing, clearPendingSharing };
};

/**
 * Set a note for a target whose view is not mounted yet — used when `AgentDrawer` mints a brand-new
 * `draft:<uuid>` and the same turn asks for an audience ("make me a chart of X, visible to everyone
 * with access to this client"). The note waits on the draft handle until the draft view mounts, and
 * the draft's first save applies it.
 */
export const seedPendingSharing = (
  queryClient: QueryClient,
  company: string,
  target: ChartHandle | string,
  request: SharingRequest
): void => {
  queryClient.setQueryData(pendingSharingKey(company, target), request);
};
